import React from 'react';
import PropTypes from 'prop-types';
import { useUser } from '../contexts/UserContext';
import { useChatMessages } from '../hooks/useChatMessages';
import { useOpenInCanvas } from '../contexts/GlobalCanvasContext';
import FeedbackButtons from './FeedbackButtons';
import MarkdownMessage from './MarkdownMessage';

interface LexChatProps {
  fullScreen?: boolean;
}

const LexChat: React.FC<LexChatProps> = ({ fullScreen = false }) => {
  const { userProfile, profilePhotoUrl } = useUser();
  const [conversationId] = React.useState(() => `conv_${Date.now()}_${Math.random()}`);
  const { openPerson, openOpportunity, openFile } = useOpenInCanvas();

  const {
    messages,
    inputMessage,
    setInputMessage,
    isLoading,
    messagesEndRef,
    textareaRef,
    sendMessage,
    handleKeyPress,
    handleTextareaChange,
    clearMessages
  } = useChatMessages({ userProfile, conversationId });

  // Helper to parse client detail messages
  const parseClientDetail = (message: string) => {
    if (message.startsWith('CLIENT_DETAIL:')) {
      try {
        const jsonStr = message.substring('CLIENT_DETAIL:'.length);
        return JSON.parse(jsonStr);
      } catch (e) {
        console.error('Failed to parse client detail:', e);
        return null;
      }
    }
    return null;
  };

  // Helper to parse JSON response from orchestrator (which now returns {response, queryId, timestamp, queryType})
  const parseOrchestratorResponse = (message: string) => {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(message);
      if (parsed.response && parsed.queryId) {
        return {
          response: parsed.response,
          queryId: parsed.queryId,
          queryType: parsed.queryType,
          timestamp: parsed.timestamp
        };
      }
    } catch (e) {
      // If not JSON, treat as plain text (backwards compatibility)
    }
    return null;
  };

  // Track previous user message for feedback linking
  const getPreviousUserMessage = (currentIndex: number) => {
    // Find the most recent user message before this assistant message
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        return messages[i].message;
      }
    }
    return '';
  };



  const containerStyle: React.CSSProperties = fullScreen ? {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '70vh',
    backgroundColor: 'var(--obsidian-sidebar-bg)',
    borderRadius: '12px',
    border: '1px solid var(--obsidian-border)',
    padding: '1.5rem',
    gap: '1rem'
  } : {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '16px',
    gap: '16px'
  };

  const messagesStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    ...(fullScreen ? { padding: '0' } : { padding: '0' })
  };

  const inputAreaStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    ...(fullScreen ? { marginTop: '1rem' } : {})
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    resize: 'none' as const,
    borderRadius: '8px',
    backgroundColor: 'var(--obsidian-bg)',
    border: '1px solid var(--obsidian-border)',
    color: 'var(--obsidian-text-primary)',
    padding: '12px 16px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    ...(fullScreen ? {
      minHeight: '120px',
      fontSize: '16px'
    } : {
      minHeight: '48px',
      fontSize: '14px'
    })
  };

  return (
    <div style={containerStyle}>
      {/* Header - only show in sidebar mode */}
      {!fullScreen && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '8px',
          paddingBottom: '8px',
          borderBottom: '1px solid var(--obsidian-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '24px', 
              height: '24px',
              borderRadius: '50%',
              backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuCTXhWQJs6jwIdYfW-ctkf35Q0A8OrrpCbqQYb_xXGDbrDt0HNOcZYZiyomeBCgcLuXSxPWEQ5AO9xUM24uPhKwXW-rRSaiM81hiHXd8gsS2nYfQgIQpi-nwn-916Zf17XraTzdpqdyE7wwKRga7amzXxGSq_qbHMST8Qh6hWmhgPDbxdsnRX2wHTVLl-mPvbkevJukTk4hNONEleWmNgr9EMLwafGLLlMvJXCjCq2HCXH7O8VIF0b8AC0dRXxu_p1caSWmsZ5WP_8")',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
            <p style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--obsidian-text-secondary)'
            }}>
              Lex AI
            </p>
          </div>
          {messages.length > 0 && (
            <button
              onClick={clearMessages}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                background: 'transparent',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '4px',
                color: 'var(--obsidian-text-secondary)',
                cursor: 'pointer',
                transition: 'all 150ms'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--obsidian-item-hover)';
                e.currentTarget.style.color = 'var(--obsidian-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--obsidian-text-secondary)';
              }}
            >
              Clear Chat
            </button>
          )}
        </div>
      )}
      
      {/* Chat Messages */}
      <div style={messagesStyle}>
        {messages.map((message, messageIndex) => {
          // Parse orchestrator response for feedback
          const orchestratorData = message.sender === 'lex' ? parseOrchestratorResponse(message.message) : null;
          const displayMessage = orchestratorData ? orchestratorData.response : message.message;
          const previousUserMessage = message.sender === 'lex' ? getPreviousUserMessage(messageIndex) : '';
          
          return (
            <div key={message.id} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: fullScreen ? '32px' : '24px',
                height: fullScreen ? '32px' : '24px',
                borderRadius: '50%',
                flexShrink: 0,
                ...(message.sender === 'user' ? {
                  backgroundImage: profilePhotoUrl ? `url(${profilePhotoUrl})` : 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuDlKmb21St0Xjpr9osbk3_VmMFa0dN1JWotpI25B5HKQR-IS3vavjbikto3ih1gZbUweT0PBuf40XP-DatCOhrE_fmCrNoCG-wSDRnXK5FwWhhhEFPvml8c9MSnm_SKjlxZtaWyGwuYmdyakygLxyPo7F517a-G3KtXeDWZko-SbpsbkXjM9-l3iXrOyScCZctd-Z1bV3r0hm_N2f4ZG7Y39aPnkZd-qWWqukxX7f7fE0VLy7P33TRTgGdHFrPcqgN5jYqs99v6a1mz")'
                } : {
                  backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuCTXhWQJs6jwIdYfW-ctkf35Q0A8OrrpCbqQYb_xXGDbrDt0HNOcZYZiyomeBCgcLuXSxPWEQ5AO9xUM24uPhKwXW-rRSaiM81hiHXd8gsS2nYfQgIQpi-nwn-916Zf17XraTzdpqdyE7wwKRga7amzXxGSq_qbHMST8Qh6hWmhgPDbxdsnRX2wHTVLl-mPvbkevJukTk4hNONEleWmNgr9EMLwafGLLlMvJXCjCq2HCXH7O8VIF0b8AC0dRXxu_p1caSWmsZ5WP_8")'
                }),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }} />
              
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <p style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--obsidian-text-secondary)',
                  margin: 0
                }}>
                  {message.sender === 'lex' ? 'Lex AI' : (userProfile?.givenName || 'You')}
                </p>
                
                {(() => {
                  const clientDetail = message.sender === 'lex' ? parseClientDetail(displayMessage) : null;
                  
                  if (clientDetail) {
                  // Render simple client detail
                  return (
                    <div style={{
                      backgroundColor: 'var(--obsidian-sidebar-bg)',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '8px',
                      border: '1px solid var(--obsidian-border)'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <strong style={{ fontSize: '16px' }}>{clientDetail.client.name}</strong>
                        {clientDetail.client.email && <div style={{ fontSize: '14px', color: 'var(--obsidian-text-secondary)' }}>{clientDetail.client.email}</div>}
                        {clientDetail.client.phone && <div style={{ fontSize: '14px', color: 'var(--obsidian-text-secondary)' }}>{clientDetail.client.phone}</div>}
                      </div>
                      {clientDetail.deal && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--obsidian-border)' }}>
                          <div><strong>Deal:</strong> {clientDetail.deal.loanType}</div>
                          {clientDetail.deal.loanAmount && <div><strong>Amount:</strong> ${clientDetail.deal.loanAmount.toLocaleString()}</div>}
                        </div>
                      )}
                      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => openPerson({
                            id: clientDetail.client.id,
                            name: clientDetail.client.name,
                            email: clientDetail.client.email,
                            phone: clientDetail.client.phone,
                            ...clientDetail.client
                          })}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            backgroundColor: 'var(--obsidian-accent)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          View Client
                        </button>
                        {clientDetail.deal && (
                          <button
                            onClick={() => openOpportunity({
                              id: clientDetail.deal.id,
                              name: `${clientDetail.deal.clientName} - ${clientDetail.deal.loanType}`,
                              title: `${clientDetail.deal.clientName} - Deal`,
                              ...clientDetail.deal
                            })}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRadius: '6px',
                              backgroundColor: 'var(--obsidian-sidebar-bg)',
                              color: 'var(--obsidian-text-primary)',
                              border: '1px solid var(--obsidian-border)',
                              cursor: 'pointer'
                            }}
                          >
                            View Deal
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
                
                // Regular message
                return (
                  <div style={{
                    backgroundColor: message.sender === 'user' ? 'var(--obsidian-accent)' : 'var(--obsidian-bg)',
                    color: message.sender === 'user' ? 'white' : 'var(--obsidian-text-primary)',
                    borderRadius: message.sender === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    padding: '12px 16px',
                    fontSize: fullScreen ? '16px' : '14px',
                    lineHeight: '1.5',
                    maxWidth: '100%',
                    ...(message.sender === 'user' ? { whiteSpace: 'pre-wrap' as const } : {})
                  }}>
                    {message.sender === 'user' ? displayMessage : <MarkdownMessage content={displayMessage} />}
                  </div>
                );
              })()}
              
              {/* Show agent executions for transparency */}
              {message.agentExecutions && message.agentExecutions.length > 0 && (
                <div style={{
                  fontSize: '11px', 
                  color: 'var(--obsidian-text-secondary)', 
                  marginTop: '4px',
                  paddingLeft: '12px'
                }}>
                  <p style={{ marginBottom: '4px', fontWeight: 500 }}>Agent Activity:</p>
                  {message.agentExecutions.map((execution, index) => (
                    <div key={index} style={{ marginBottom: '2px' }}>
                      <span style={{ fontWeight: 500 }}>{execution.agentName}</span>: {execution.purpose}
                      {execution.executionTime && <span style={{ marginLeft: '4px', opacity: 0.7 }}>({execution.executionTime}ms)</span>}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Feedback buttons for assistant messages with queryId */}
              {message.sender === 'lex' && orchestratorData && orchestratorData.queryId && (
                <FeedbackButtons
                  queryId={orchestratorData.queryId}
                  userQuery={previousUserMessage}
                  assistantResponse={displayMessage}
                />
              )}
            </div>
          </div>
          );
        })}
        
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              width: fullScreen ? '32px' : '24px',
              height: fullScreen ? '32px' : '24px',
              borderRadius: '50%',
              flexShrink: 0,
              backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuCTXhWQJs6jwIdYfW-ctkf35Q0A8OrrpCbqQYb_xXGDbrDt0HNOcZYZiyomeBCgcLuXSxPWEQ5AO9xUM24uPhKwXW-rRSaiM81hiHXd8gsS2nYfQgIQpi-nwn-916Zf17XraTzdpqdyE7wwKRga7amzXxGSq_qbHMST8Qh6hWmhgPDbxdsnRX2wHTVLl-mPvbkevJukTk4hNONEleWmNgr9EMLwafGLLlMvJXCjCq2HCXH7O8VIF0b8AC0dRXxu_p1caSWmsZ5WP_8")',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
            
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--obsidian-text-secondary)',
                margin: 0
              }}>
                Lex AI
              </p>
              
              <div style={{
                backgroundColor: 'var(--obsidian-bg)',
                color: 'var(--obsidian-text-primary)',
                borderRadius: '12px 12px 12px 4px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div className="loading-dots" style={{ transform: 'scale(0.8)' }}>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Example Prompts - Show above input when no messages */}
      {messages.length === 0 && !fullScreen && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          marginBottom: '8px'
        }}>
          <p style={{
            fontSize: '11px',
            color: 'var(--obsidian-text-secondary)',
            margin: 0,
            paddingLeft: '4px'
          }}>
            Try asking:
          </p>
          {[
            "Show me John Smith's details",
            "What deals are in settlement?",
            "Show overdue tasks",
            "Summarize this week's activity"
          ].map((prompt, idx) => (
            <button
              key={idx}
              onClick={() => {
                handleTextareaChange({ target: { value: prompt } } as any);
                setTimeout(() => sendMessage(), 50);
              }}
              style={{
                padding: '6px 10px',
                fontSize: '11px',
                textAlign: 'left',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '6px',
                color: 'var(--obsidian-text-primary)',
                cursor: 'pointer',
                transition: 'all 150ms'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              }}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div style={inputAreaStyle}>
        <textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={handleTextareaChange}
          onKeyPress={handleKeyPress}
          placeholder={fullScreen ? "Ask Lex AI anything about your clients, deals, tasks, or documents..." : "Ask AI..."}
          style={textareaStyle}
          disabled={isLoading}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--obsidian-accent)';
            e.target.style.boxShadow = '0 0 0 1px var(--obsidian-accent)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--obsidian-border)';
            e.target.style.boxShadow = 'none';
          }}
        />
        
        {fullScreen && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: 'var(--obsidian-text-secondary)',
              cursor: 'pointer'
            }}>
              <span className="material-symbols-outlined">attach_file</span>
            </button>
          </div>
        )}
        
        <button
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading}
          style={{
            width: fullScreen ? '48px' : '32px',
            height: fullScreen ? '48px' : '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (!inputMessage.trim() || isLoading) ? 'var(--obsidian-border)' : 'var(--obsidian-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (!inputMessage.trim() || isLoading) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            flexShrink: 0
          }}
        >
          <span className="material-symbols-outlined">
            {isLoading ? 'hourglass_empty' : 'arrow_upward'}
          </span>
        </button>
      </div>
    </div>
  );
};

LexChat.propTypes = {
  fullScreen: PropTypes.bool
};

export default LexChat;
